import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { EnhancedHeader } from '@/components/ui/enhanced-header';
import { Coins, Gem, Spade, Gift, Calendar, Check, Clock, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';

interface Award {
  id: number;
  awardType: 'CURRENCY' | 'DECK' | 'SHOP_ITEM';
  currencyType?: 'COINS' | 'GEMS';
  amount?: number;
  reason?: string;
  isClaimed: boolean;
  claimedAt?: string;
  createdAt: string;
  deck?: { name: string };
  shopItem?: { name: string; itemType: string };
}

export default function AllAwardsPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['player-awards-all'],
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/awards/player/awards/all');
      const result = await response.json();
      return result.awards ?? [];
    },
  });

  const awards: Award[] = data ?? [];

  const getAwardIcon = (award: Award) => {
    switch (award.awardType) {
      case "CURRENCY":
        if (award.currencyType === "GEMS") {
          return <Gem className="h-8 w-8 text-purple-400" />;
        }
        return <Coins className="h-8 w-8 text-yellow-500" />;
      case "DECK":
        return <Spade className="h-8 w-8 text-purple-500" />;
      case "SHOP_ITEM":
        return <Gift className="h-8 w-8 text-blue-500" />;
      default:
        return <Gift className="h-8 w-8" />;
    }
  };

  const getAwardDescription = (award: Award) => {
    switch (award.awardType) {
      case "CURRENCY":
        return `${award.amount} ${award.currencyType === "GEMS" ? "Gems" : "Coins"}`;
      case "DECK":
        return `Deck: ${award.deck?.name ?? "Custom Deck"}`;
      case "SHOP_ITEM":
        return `Item: ${award.shopItem?.name ?? "Shop Item"}`;
      default:
        return "Special Award";
    }
  };

  const getStatusBadge = (award: Award) => {
    if (award.isClaimed) {
      return (
        <Badge variant="outline" className="bg-green-500/10 border-green-500/20 text-green-400">
          <Check className="h-3 w-3 mr-1" />
          Claimed
        </Badge>
      );
    } else {
      return (
        <Badge variant="outline" className="bg-amber-500/10 border-amber-500/20 text-amber-400">
          <Clock className="h-3 w-3 mr-1" />
          Pending
        </Badge>
      );
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-black">
        <EnhancedHeader
          title="All Awards"
          backPath="/games"
          coloredText={true}
        />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-blue-400" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-black">
        <EnhancedHeader
          title="All Awards"
          backPath="/games"
          coloredText={true}
        />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center text-red-400">
            <p>Failed to load awards. Please try again later.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-black">
      <EnhancedHeader
        title="All Awards"
        backPath="/games"
        coloredText={true}
      />
      
      <div className="container mx-auto px-4 py-8">
        {awards.length === 0 ? (
          <div className="text-center py-20">
            <Gift className="h-16 w-16 mx-auto text-gray-500 mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">No Awards Yet</h2>
            <p className="text-gray-400">You haven't received any awards yet. Keep playing to earn rewards!</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="mb-6">
              <h2 className="text-xl font-bold text-white mb-2">Your Awards History</h2>
              <p className="text-gray-400">
                Total awards: {awards.length} • 
                Claimed: {awards.filter(a => a.isClaimed).length} • 
                Pending: {awards.filter(a => !a.isClaimed).length}
              </p>
            </div>

            {awards.map((award) => (
              <Card key={award.id} className="bg-black/40 border-blue-500/20 backdrop-blur-sm">
                <CardContent className="p-4">
                  <div className="flex items-center space-x-4">
                    <div className="p-3 bg-blue-900/30 rounded-full">
                      {getAwardIcon(award)}
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-lg font-semibold text-white">
                          {getAwardDescription(award)}
                        </h3>
                        {getStatusBadge(award)}
                      </div>
                      
                      {award.reason && (
                        <p className="text-sm text-gray-400 mb-2">{award.reason}</p>
                      )}
                      
                      <div className="flex items-center text-xs text-gray-500 space-x-4">
                        <div className="flex items-center">
                          <Calendar className="h-3 w-3 mr-1" />
                          Received: {format(new Date(award.createdAt), 'MMM d, yyyy')}
                        </div>
                        {award.isClaimed && award.claimedAt && (
                          <div className="flex items-center">
                            <Check className="h-3 w-3 mr-1" />
                            Claimed: {format(new Date(award.claimedAt), 'MMM d, yyyy')}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
